import { useState, useEffect, useCallback, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function useBatchImportStatus(authHeaders) {
  const [activeBatches, setActiveBatches] = useState([]);
  const [batchDetails, setBatchDetails] = useState({});
  const [isPolling, setIsPolling] = useState(false);
  const intervalRef = useRef(null);
  // Timestamp until which late-resolving fetches are ignored (set on cancelAll)
  // so an in-flight poll can't re-populate the panel right after cancellation.
  const suppressUntilRef = useRef(0);

  const fetchActive = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/rag/cdm/url-import/active`, {
        headers: authHeaders,
      });
      if (res.ok) {
        const data = await res.json();
        if (Date.now() < suppressUntilRef.current) return false;
        setActiveBatches(data.active_batches || []);
        return (data.active_batches || []).length > 0;
      }
    } catch (e) { /* silent */ }
    return false;
  }, [authHeaders]);

  const fetchBatchDetail = useCallback(async (batchId) => {
    try {
      const res = await fetch(`${API_URL}/api/rag/cdm/url-import/batch/${batchId}`, {
        headers: authHeaders,
      });
      if (res.ok) {
        const data = await res.json();
        if (Date.now() < suppressUntilRef.current) return;
        setBatchDetails(prev => ({ ...prev, [batchId]: data }));
      }
    } catch (e) { /* silent */ }
  }, [authHeaders]);

  const startPolling = useCallback(() => {
    if (intervalRef.current) return;
    setIsPolling(true);

    const poll = async () => {
      if (document.visibilityState === 'hidden') return;
      const hasActive = await fetchActive();
      if (!hasActive) {
        stopPolling();
      }
    };

    poll();
    intervalRef.current = setInterval(poll, 3000);
  }, [fetchActive]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPolling(false);
  }, []);

  const cancelAll = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/rag/cdm/url-import/cancel-all`, {
        method: 'POST',
        headers: authHeaders,
      });
      if (res.ok) {
        const data = await res.json();
        // Ignore any in-flight polls/detail fetches for the next few seconds so
        // the cleared panel can't be re-populated by a late-resolving request.
        suppressUntilRef.current = Date.now() + 5000;
        // Clear the panel immediately — cancelled rows are terminal
        setActiveBatches([]);
        setBatchDetails({});
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        setIsPolling(false);
        return data;
      }
    } catch (e) { /* silent */ }
    return null;
  }, [authHeaders]);

  // Auto-fetch details when active batches change
  useEffect(() => {
    activeBatches.forEach(batch => {
      if (batch.batch_id) fetchBatchDetail(batch.batch_id);
    });
  }, [activeBatches, fetchBatchDetail]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // Pause on tab hidden
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'hidden' && intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      } else if (document.visibilityState === 'visible' && isPolling) {
        intervalRef.current = setInterval(async () => {
          const hasActive = await fetchActive();
          if (!hasActive) stopPolling();
        }, 3000);
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [isPolling, fetchActive, stopPolling]);

  return {
    activeBatches,
    batchDetails,
    isPolling,
    startPolling,
    stopPolling,
    cancelAll,
    fetchBatchDetail,
    refreshActive: fetchActive,
  };
}
